import React, { useEffect, useState } from 'react';
import { SelectProps } from '@material-ui/core/Select';
import { TextField } from '@material-ui/core';
import { TranslationsTypes } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { useDebounce } from '@app/helpers';
import { useSelector } from 'react-redux';
import { InputProps } from '@material-ui/core/Input';
import { menuPropsV2, useLabelStylesV2, useSelectIconV2, useSelectStylesV2, menuPaperStylesV2 } from '@app/componentsV2/Forms/Select/theme';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TextFieldProps } from '@material-ui/core/TextField';

interface IProps extends SelectProps {
    label?: TranslationsTypes;
    totalCount: number;
    currentCount: number;
    onFormChange?: (e: any) => void;
    onFetch: (countToUse: number, currentOrgId: string, isReset: boolean) => void;
    reset?: boolean;
}

export const SelectInfiniteV2 = (props: IProps & TextFieldProps) => {
    const { totalCount, currentCount, onFetch, reset, placeholder, ...propsRest } = props;
    const { translation } = useTypedTranslation();
    const menuPaperClasses = menuPaperStylesV2();
    const defaultClasses = useSelectStylesV2();
    const labelClasses = useLabelStylesV2();
    const selectIconClasses = useSelectIconV2();
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollMax, setScrollMax] = useState(0);
    const debouncedValue = useDebounce(scrollTop, 100);
    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const resetScroll = useSelector((state: any) => state.basic.resetScroll);
    const {placeholder: placeholderClass, ...defaultClassesRest} = defaultClasses;
    const {focused, ...selectIconClassesRest} = selectIconClasses;

    useEffect(() => {
        if (debouncedValue) {
            const hasMore = currentCount < totalCount;
            const isFetchNextData = scrollMax - scrollMax / 5 < scrollTop;

            if (hasMore && isFetchNextData) {
                onFetch(currentCount, currentOrgId, false);
            }
        }
    }, [debouncedValue]);

    useEffect(() => {
        if (currentOrgId !== undefined || reset) {
            onFetch(0, currentOrgId, true);
        }
    }, [currentOrgId, resetScroll, reset]);

    useEffect(() => {
        if (!currentCount && !currentOrgId) {
            onFetch(0, currentOrgId, true);
        }
    }, [currentCount]);

    const customExpandMore = ({ className, classes, ...rest }: any) => {
        return <ExpandMoreIcon className={className} classes={selectIconClassesRest} fontSize='small' {...rest} />;
    };

    const renderPlaceholder = (selected: any) => {
        if (selected?.length === 0) {
            return (
                <div className={placeholderClass} onClick={props.onClick}>
                    {translation((placeholder as TranslationsTypes) || '')}
                </div>
            );
        }

        return selected;
    };

    const loadMoreItems = (e: any) => {
        setScrollTop(e.target.scrollTop);
        setScrollMax(e.target.scrollHeight - e.target.clientHeight);
    };

    return (
        <>
            {propsRest.label && (
                <span className={'text-field__label'}>
                    {translation((propsRest.label ?? '') as TranslationsTypes)}{' '}
                    {propsRest.required && <span className='text-field__label-asterisk'>*</span>}
                </span>
            )}
        <TextField
            select
            fullWidth={props.fullWidth}
            InputLabelProps={{
                shrink: true,
                classes: labelClasses,
                ...props.InputLabelProps
            }}
            InputProps={
                {
                    disableUnderline: true,
                    classes: defaultClassesRest,
                    ...props.InputProps
                } as Partial<InputProps>
            }
            SelectProps={{
                MenuProps: {
                    classes: { paper: menuPaperClasses.menuPaper },
                    ...menuPropsV2
                },
                IconComponent: customExpandMore,
                displayEmpty: true,
                renderValue: props.value !== '' || !placeholder ? undefined : renderPlaceholder,
                ...props.SelectProps
            }}
            onScroll={loadMoreItems}
            {...propsRest}
            label=''
        >
            {props.children}
        </TextField>
        </>
    );
};
